import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react';
import { useMutation, useQuery } from '@apollo/client';
import classNames from 'classnames';
import {
  Button,
  Card,
  ConfirmationModal,
  CREATE_DASHBOARD,
  DELETE_DASHBOARD,
  GET_DASHBOARDS,
  GET_SAVED_CHARTS,
  LEAVE_DASHBOARD,
  RENAME_DASHBOARD,
  RUN_CHART_QUERIES,
  SAVE_DASHBOARD,
  SET_DASHBOARD_OVERRIDES,
  useAlert,
} from '@employ-engage/common-components';
import {
  applyOverrides,
  chartedFieldOf,
  ChartResult,
  ChartSpec,
  CrossFilter,
} from '@employ-engage/data';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import DashboardControls, { DashboardOverrides } from './dashboard-controls';
import DashboardGrid from './dashboard-grid';
import DashboardNameModal from './dashboard-name-modal';
import DashboardShareModal from './dashboard-share-modal';
import { resolveBreakdownFor } from './spec-overrides';
import { DashboardTile, needsReconcile, reconcileTiles } from './tile-layout';
import { createdByLine, Person } from './created-by';

type SavedChart = {
  id: string;
  name: string;
  templateId: string;
  spec: ChartSpec;
  createdBy?: Person | null;
  createdAt?: string | null;
};

type DashboardRecord = {
  id: string;
  name: string;
  ownerUserId: string;
  collaboratorUserIds: string[];
  isOwner: boolean;
  createdBy?: Person | null;
  createdAt?: string | null;
  tiles: DashboardTile[];
  dateRangeOverride: Record<string, unknown> | null;
  breakdownOverride: string | null;
  updatedAt: string | null;
};

/**
 * A dashboard override never names a question, only facts about a response
 * (see DASHBOARD_BREAKDOWNS), so no field catalogue is needed to resolve one.
 */
const resolveDashboardBreakdown = resolveBreakdownFor(null, []);

export function Dashboard() {
  const { t, i18n } = useTranslation(['common']);
  const navigate = useNavigate();

  /**
   * The value a reader clicked on one tile, narrowing the others.
   *
   * The URL is the whole of its persistence, deliberately. Saving it to the
   * dashboard would bump `updatedAt`, and `saveDashboard` refuses a stale
   * write — so one person clicking a bar would reject every collaborator's
   * in-flight tile edit. A link still carries the filtered view, which is
   * what sharing one actually needs.
   */
  const [searchParams, setSearchParams] = useSearchParams();
  const crossFilter = useMemo<CrossFilter | null>(() => {
    const raw = searchParams.get('filter');
    if (!raw) return null;
    try {
      const parsed = JSON.parse(raw);
      return parsed?.field &&
        Array.isArray(parsed.values) &&
        parsed.values.length
        ? (parsed as CrossFilter)
        : null;
    } catch {
      // A hand-edited or truncated URL is not worth breaking the page over.
      return null;
    }
  }, [searchParams]);

  const setCrossFilter = (next: CrossFilter | null) => {
    const params = new URLSearchParams(searchParams);
    if (next) params.set('filter', JSON.stringify(next));
    else params.delete('filter');
    // Replace rather than push: a dashboard is not a place you want to press
    // back through one bar at a time.
    setSearchParams(params, { replace: true });
  };

  const {
    data: dashboardsData,
    loading: dashboardsLoading,
    refetch: refetchDashboards,
  } = useQuery(GET_DASHBOARDS, { fetchPolicy: 'cache-and-network' });
  const { data: chartsData, loading: chartsLoading } = useQuery(
    GET_SAVED_CHARTS,
    {
      fetchPolicy: 'cache-and-network',
    }
  );

  const [saveDashboard] = useMutation(SAVE_DASHBOARD);
  const [setOverrides] = useMutation(SET_DASHBOARD_OVERRIDES);
  const [createDashboard] = useMutation(CREATE_DASHBOARD);
  const [renameDashboard] = useMutation(RENAME_DASHBOARD);
  const [deleteDashboard] = useMutation(DELETE_DASHBOARD);
  const [leaveDashboard] = useMutation(LEAVE_DASHBOARD);

  const dashboards: DashboardRecord[] = useMemo(
    () => dashboardsData?.dashboards ?? [],
    [dashboardsData]
  );

  /**
   * Which dashboard is open, kept in the URL like the cross-filter above. A
   * link from the results page can then open the dashboard a chart was just
   * pinned to, and a reload stays where it was. An id that no longer matches
   * one of yours falls back to the first, below.
   */
  const activeId = searchParams.get('dashboard');
  const setActiveId = (id: string | null) =>
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        if (id) params.set('dashboard', id);
        else params.delete('dashboard');
        return params;
      },
      { replace: true }
    );
  const [tiles, setTiles] = useState<DashboardTile[]>([]);
  const [shareOpen, setShareOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmLeave, setConfirmLeave] = useState(false);
  /** Which naming modal is open, if either. */
  const [nameModal, setNameModal] = useState<'create' | 'rename' | null>(null);

  const {
    Alert: ConflictAlert,
    fireAlert: fireConflict,
    setAlertMessage: setConflictMessage,
  } = useAlert({
    type: 'warning',
    title: t('Reloaded'),
    message: '',
  });

  const { Alert: LeaveFailedAlert, fireAlert: fireLeaveFailed } = useAlert({
    type: 'error',
    title: t('Could not leave this dashboard'),
    message: t('Please try again.'),
  });
  const { Alert: CopiedAlert, fireAlert: fireCopied } = useAlert({
    type: 'success',
    title: t('Link copied'),
    message: t('Anyone it is shared with can open this dashboard from it.'),
  });
  const {
    Alert: CopyFailedAlert,
    fireAlert: fireCopyFailed,
    setAlertMessage: setCopyFailedMessage,
  } = useAlert({
    type: 'warning',
    title: t('Could not copy the link'),
    message: '',
  });

  const active = useMemo(
    () => dashboards.find((d) => d.id === activeId) ?? dashboards[0] ?? null,
    [dashboards, activeId]
  );

  /**
   * A link named a dashboard this person cannot see.
   *
   * The server answers "not found" both for one that was deleted and for one
   * that was never shared, deliberately, so the message covers both. Without
   * it the page quietly showed a different dashboard and the link looked
   * broken.
   */
  const linkNotShared =
    !!activeId &&
    !!dashboardsData &&
    !dashboards.some((d) => d.id === activeId);

  /**
   * The address of this dashboard, with any cross-filter on it, for pasting
   * to someone it is shared with.
   */
  const copyLink = async () => {
    if (!active) return;
    const url = new URL(window.location.href);
    url.searchParams.set('dashboard', active.id);
    try {
      await navigator.clipboard.writeText(url.toString());
      fireCopied(4000);
    } catch {
      // No clipboard outside a secure context, or permission refused: hand
      // over the link to copy by hand rather than failing silently.
      setCopyFailedMessage(<span className="break-all">{url.toString()}</span>);
      fireCopyFailed(10000);
    }
  };

  // Charts added from here should be pinned here, not to the first dashboard.
  const addChartsPath = active
    ? `../reports/report-builder?dashboard=${encodeURIComponent(active.id)}`
    : '../reports/report-builder';

  const charts: SavedChart[] = useMemo(
    () => chartsData?.savedCharts ?? [],
    [chartsData]
  );
  const chartById = useMemo(
    () => new Map(charts.map((chart) => [chart.id, chart])),
    [charts]
  );

  // The server is the source of truth until the user touches something, and
  // again every time the dashboard being looked at changes.
  const touched = useRef(false);
  const loadedId = useRef<string | null>(null);
  useEffect(() => {
    if (!active) return;
    if (loadedId.current === active.id && touched.current) return;
    loadedId.current = active.id;
    touched.current = false;
    setTiles((active.tiles ?? []).map((tile) => ({ ...tile })));
  }, [active]);

  /**
   * Saves the whole array, and never more than one request at a time.
   *
   * Two drags in quick succession would otherwise race, and the one that
   * happened to land second would win regardless of which the user did last.
   * The latest layout is queued instead and sent when the in-flight save
   * returns, so the last thing the user did is the thing that sticks.
   *
   * `expectedUpdatedAt` is what makes sharing safe: the whole tile array is
   * written at once, so without it a collaborator's additions would be
   * discarded wholesale rather than conflicting on one tile. On a refusal the
   * dashboard is re-read and the user is told, rather than either side's work
   * vanishing quietly.
   */
  const inFlight = useRef(false);
  const queued = useRef<DashboardTile[] | null>(null);

  const flush = useCallback(
    async (next: DashboardTile[]) => {
      if (!active) return;
      if (inFlight.current) {
        queued.current = next;
        return;
      }
      inFlight.current = true;
      try {
        await saveDashboard({
          variables: {
            id: active.id,
            tiles: next.map(toTileInput),
            expectedUpdatedAt: active.updatedAt,
          },
        });
        await refetchDashboards();
      } catch (error: any) {
        queued.current = null;
        touched.current = false;
        setConflictMessage(
          error?.message ??
            t('Somebody else changed this dashboard. Reloading it.')
        );
        fireConflict(6000);
        await refetchDashboards();
      } finally {
        inFlight.current = false;
        const pending = queued.current;
        queued.current = null;
        if (pending) await flush(pending);
      }
    },
    [
      active,
      saveDashboard,
      refetchDashboards,
      setConflictMessage,
      fireConflict,
      t,
    ]
  );

  const applyChange = useCallback(
    (next: DashboardTile[]) => {
      touched.current = true;
      setTiles(next);
      void flush(next);
    },
    [flush]
  );

  // A colleague can delete a chart that is pinned to this dashboard. Drop the
  // orphaned tiles once the chart list is actually known — not while it is
  // still loading, which would wipe the dashboard on a slow request.
  useEffect(() => {
    if (chartsLoading || !chartsData) return;
    const availableIds = charts.map((chart) => chart.id);
    if (!needsReconcile(tiles, availableIds)) return;
    const next = reconcileTiles(tiles, availableIds);
    setTiles(next);
    void flush(next);
  }, [charts, chartsLoading, chartsData, tiles, flush]);

  /**
   * Each tile's spec, re-scoped by the dashboard's own controls.
   *
   * The tile's `dateRangeOverride` is applied first and the dashboard's on top
   * of it: a dashboard-wide period is the whole point of the control, so it
   * wins over what a single tile was pinned with, and clearing it returns
   * every tile to its own.
   */
  const overridden = useMemo(
    () =>
      tiles.map((tile) => {
        const chart = chartById.get(tile.chartId);
        if (!chart) return null;

        const pinned = tile.dateRangeOverride
          ? ({ ...chart.spec, dateRange: tile.dateRangeOverride } as ChartSpec)
          : chart.spec;

        return applyOverrides(
          pinned,
          {
            dateRange: (active?.dateRangeOverride as never) ?? null,
            breakdown: active?.breakdownOverride ?? null,
            crossFilter,
            tileId: tile.id,
          },
          resolveDashboardBreakdown
        );
      }),
    [tiles, chartById, active, crossFilter]
  );

  const specs = useMemo(
    () => overridden.filter(Boolean).map((outcome) => outcome!.spec),
    [overridden]
  );

  /**
   * A click on a bar or a slice filters the rest of the dashboard by it.
   *
   * The field comes from the tile's own spec, so the filter names the
   * question that was actually charted. Clicking the same value again clears
   * it: the way out has to be the way in, or the only escape is the header.
   */
  const selectCategory = (tile: DashboardTile, category: string) => {
    const spec = chartById.get(tile.chartId)?.spec as ChartSpec | undefined;
    const field = spec ? chartedFieldOf(spec) : null;
    if (!field) return;

    const sameAgain =
      crossFilter?.values.length === 1 && crossFilter.values[0] === category;
    setCrossFilter(
      sameAgain ? null : { field, values: [category], sourceTileId: tile.id }
    );
  };

  const noteByTileId = useMemo(() => {
    const map = new Map<string, string | null>();
    tiles.forEach((tile, index) => {
      map.set(
        tile.id,
        overridden[index]?.breakdownRefusedBecause ??
          overridden[index]?.crossFilterRefusedBecause ??
          null
      );
    });
    return map;
  }, [tiles, overridden]);

  const specByTileId = useMemo(() => {
    const map = new Map<string, ChartSpec>();
    tiles.forEach((tile, index) => {
      const outcome = overridden[index];
      if (outcome) map.set(tile.id, outcome.spec);
    });
    return map;
  }, [tiles, overridden]);

  // Every tile in one call: tiles sharing a survey and window are read once.
  const {
    data: resultsData,
    loading: resultsLoading,
    error: resultsError,
  } = useQuery(RUN_CHART_QUERIES, {
    variables: { specs },
    skip: !specs.length,
    fetchPolicy: 'cache-and-network',
  });

  const resultsByTileId = useMemo(() => {
    const results: ChartResult[] = resultsData?.runChartQueries ?? [];
    const map = new Map<string, ChartResult>();
    let i = 0;
    tiles.forEach((tile) => {
      if (!chartById.has(tile.chartId)) return;
      map.set(tile.id, results[i]);
      i += 1;
    });
    return map;
  }, [resultsData, tiles, chartById]);

  const saveOverrides = async (next: DashboardOverrides) => {
    if (!active) return;
    await setOverrides({
      variables: {
        id: active.id,
        dateRangeOverride: next.dateRange,
        breakdownOverride: next.breakdown,
      },
    });
    await refetchDashboards();
  };

  const addDashboard = async (name: string) => {
    const { data } = await createDashboard({ variables: { name } });
    await refetchDashboards();
    // Land on the new one: creating a dashboard and then having to go and
    // find it is not what the action implies.
    touched.current = false;
    setActiveId(data?.createDashboard?.id ?? null);
  };

  const rename = async (name: string) => {
    if (!active) return;
    await renameDashboard({ variables: { id: active.id, name } });
    await refetchDashboards();
  };

  const remove = async () => {
    if (!active) return;
    await deleteDashboard({ variables: { id: active.id } });
    setActiveId(null);
    await refetchDashboards();
  };

  const leave = async () => {
    if (!active) return;
    try {
      await leaveDashboard({ variables: { id: active.id } });
    } catch (error) {
      console.error('Could not leave the dashboard', error);
      fireLeaveFailed(6000);
      return;
    }
    // Back to your own first dashboard: the one just left is gone from the
    // switcher, and its id in the address would now say "not shared".
    setActiveId(null);
    await refetchDashboards();
  };

  if (dashboardsLoading && !dashboards.length) {
    return <Card>{t('Loading your dashboard…')}</Card>;
  }

  /**
   * The dashboard you are looking at, and what can be done to it.
   *
   * A switcher that reads as the page's title rather than a form control, with
   * everything else behind one menu. Four link-styled buttons in a row read as
   * a paragraph of links, gave no sense of which was destructive, and put
   * Delete one careless pixel from Rename.
   */
  const header = (
    <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
      <div className="flex min-w-0 items-center gap-2">
        <Menu as="div" className="relative">
          <MenuButton className="group flex max-w-full items-center gap-2 rounded-lg px-2 py-1 text-left hover:bg-light-alternate focus:outline-none focus:ring-2 focus:ring-light-primary">
            <span className="truncate text-2xl font-bold text-gray-900 dark:text-gray-100">
              {active?.name ?? t('Dashboard')}
            </span>
            <i
              className="fa-solid fa-chevron-down text-sm text-light-secondaryText"
              aria-hidden
            />
          </MenuButton>
          {active?.createdBy && (
            <p className="truncate px-2 text-xs text-light-secondaryText">
              {createdByLine(
                t,
                i18n.language,
                active.createdBy,
                active.createdAt
              )}
            </p>
          )}
          <MenuItems
            anchor="bottom start"
            className="z-20 mt-1 w-72 rounded-xl bg-white p-1 shadow-lg ring-1 ring-black/5 focus:outline-none dark:bg-dark-primaryBackground"
          >
            {dashboards.map((dashboard) => (
              <MenuItem key={dashboard.id}>
                <button
                  type="button"
                  onClick={() => {
                    touched.current = false;
                    setActiveId(dashboard.id);
                  }}
                  className={classNames(
                    'flex w-full items-center justify-between gap-2 rounded-lg px-3 py-2 text-left text-sm data-[focus]:bg-light-alternate',
                    dashboard.id === active?.id
                      ? 'font-semibold text-light-primary'
                      : 'text-light-primaryText'
                  )}
                >
                  <span className="flex min-w-0 flex-col">
                    <span className="truncate">{dashboard.name}</span>
                    {dashboard.createdBy && (
                      <span className="truncate text-xs font-normal text-light-secondaryText">
                        {createdByLine(
                          t,
                          i18n.language,
                          dashboard.createdBy,
                          dashboard.createdAt
                        )}
                      </span>
                    )}
                  </span>
                  {!dashboard.isOwner && (
                    <span className="shrink-0 rounded-full bg-light-alternate px-2 py-0.5 text-[10px] uppercase tracking-wide text-light-secondaryText">
                      {t('Shared')}
                    </span>
                  )}
                </button>
              </MenuItem>
            ))}
            <div className="my-1 border-t border-gray-200 dark:border-gray-700" />
            <MenuItem>
              <button
                type="button"
                onClick={() => setNameModal('create')}
                className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm text-light-primaryText data-[focus]:bg-light-alternate"
              >
                <i className="fa-solid fa-plus text-xs" aria-hidden />
                {t('New dashboard')}
              </button>
            </MenuItem>
          </MenuItems>
        </Menu>

        {active && !active.isOwner && (
          <span className="rounded-full bg-light-alternate px-2 py-0.5 text-xs text-light-secondaryText">
            {t('Shared with you')}
          </span>
        )}

        {active && (
          <Menu as="div" className="relative">
            <MenuButton
              aria-label={t('Dashboard options')}
              className="rounded-lg px-2 py-2 text-light-secondaryText hover:bg-light-alternate focus:outline-none focus:ring-2 focus:ring-light-primary"
            >
              <i className="fa-solid fa-ellipsis" aria-hidden />
            </MenuButton>
            <MenuItems
              anchor="bottom start"
              className="z-20 mt-1 w-56 rounded-xl bg-white p-1 shadow-lg ring-1 ring-black/5 focus:outline-none dark:bg-dark-primaryBackground"
            >
              <MenuItem>
                <button
                  type="button"
                  onClick={() => void copyLink()}
                  className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left text-sm text-light-primaryText data-[focus]:bg-light-alternate"
                >
                  <i className="fa-solid fa-link w-4 text-xs" aria-hidden />
                  {t('Copy link')}
                </button>
              </MenuItem>
              {/* Renaming, sharing and deleting stay with the owner; a
                  collaborator gets the link and the way out. */}
              {!active.isOwner && (
                <>
                  <div className="my-1 border-t border-gray-200 dark:border-gray-700" />
                  <MenuItem>
                    <button
                      type="button"
                      onClick={() => setConfirmLeave(true)}
                      className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left text-sm text-red-600 data-[focus]:bg-red-50"
                    >
                      <i
                        className="fa-solid fa-right-from-bracket w-4 text-xs"
                        aria-hidden
                      />
                      {t('Leave dashboard')}
                    </button>
                  </MenuItem>
                </>
              )}
              {active.isOwner && (
                <>
                  <MenuItem>
                    <button
                      type="button"
                      onClick={() => setNameModal('rename')}
                      className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left text-sm text-light-primaryText data-[focus]:bg-light-alternate"
                    >
                      <i className="fa-solid fa-pen w-4 text-xs" aria-hidden />
                      {t('Rename')}
                    </button>
                  </MenuItem>
                  <MenuItem>
                    <button
                      type="button"
                      onClick={() => setShareOpen(true)}
                      className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left text-sm text-light-primaryText data-[focus]:bg-light-alternate"
                    >
                      <i
                        className="fa-solid fa-user-plus w-4 text-xs"
                        aria-hidden
                      />
                      {t('Share')}
                      {!!active.collaboratorUserIds?.length && (
                        <span className="ml-auto text-xs text-light-secondaryText">
                          {active.collaboratorUserIds.length}
                        </span>
                      )}
                    </button>
                  </MenuItem>
                  {/* Separated and coloured: it is the one item here that cannot
                  be undone, and it used to sit flush against Rename. */}
                  <div className="my-1 border-t border-gray-200 dark:border-gray-700" />
                  <MenuItem>
                    <button
                      type="button"
                      onClick={() => setConfirmDelete(true)}
                      className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left text-sm text-red-600 data-[focus]:bg-red-50"
                    >
                      <i
                        className="fa-solid fa-trash w-4 text-xs"
                        aria-hidden
                      />
                      {t('Delete')}
                    </button>
                  </MenuItem>
                </>
              )}
            </MenuItems>
          </Menu>
        )}
      </div>

      <Button variant="primary" onClick={() => navigate(addChartsPath)}>
        <i className="fa-solid fa-plus mr-2 text-xs" aria-hidden />
        {t('Add charts')}
      </Button>
    </div>
  );

  /**
   * A cross-filter changes every number on the page, so it is never left to
   * be inferred from the charts. It says what is filtered and how to stop —
   * an invisible filter is how somebody reads a slice of the data as the
   * whole of it.
   */
  const filterBanner = crossFilter ? (
    <div className="mb-4 flex flex-wrap items-center gap-2 rounded-lg bg-light-alternate px-3 py-2 text-sm dark:bg-dark-alternate">
      <i className="fa-solid fa-filter text-xs" aria-hidden />
      <span>
        {t('Filtered to {{value}}', { value: crossFilter.values.join(', ') })}
      </span>
      <span className="text-light-secondaryText dark:text-dark-secondaryText">
        {t('from {{question}}', {
          question:
            (crossFilter.field as { label?: string }).label ?? t('a chart'),
        })}
      </span>
      <Button variant="link" onClick={() => setCrossFilter(null)}>
        {t('Clear filter')}
      </Button>
    </div>
  ) : null;

  return (
    <div>
      <ConflictAlert />
      <CopiedAlert />
      <LeaveFailedAlert />
      <CopyFailedAlert />
      {header}
      {linkNotShared && (
        <p
          role="status"
          className="mb-5 rounded-lg bg-amber-50 px-4 py-3 text-sm text-amber-800"
        >
          {t("This dashboard isn't shared with you, or it no longer exists.")}
          {active &&
            ` ${t('Showing “{{name}}” instead.', { name: active.name })}`}
        </p>
      )}
      {filterBanner}

      {active && (
        <div className="mb-5">
          <DashboardControls
            overrides={{
              dateRange: active.dateRangeOverride ?? null,
              breakdown: active.breakdownOverride ?? null,
            }}
            onChange={(next) => void saveOverrides(next)}
          />
        </div>
      )}

      {!tiles.length ? (
        <Card className="flex flex-col items-center justify-center gap-3 py-16 text-center">
          <i
            className="fa-solid fa-chart-line text-3xl text-gray-300"
            aria-hidden
          />
          <h2 className="text-lg font-semibold">
            {t('Your dashboard is empty')}
          </h2>
          <p className="max-w-md text-sm text-light-secondaryText dark:text-dark-secondaryText">
            {t(
              'Open a survey’s results, then pin the charts you want to keep an eye on.'
            )}
          </p>
          <button
            type="button"
            onClick={() => navigate(addChartsPath)}
            className="mt-2 rounded-md bg-light-primary px-4 py-2 text-sm font-medium text-white"
          >
            {t('View Survey Results')}
          </button>
        </Card>
      ) : (
        <DashboardGrid
          onSelect={selectCategory}
          tiles={tiles}
          loading={resultsLoading}
          error={resultsError ? t('This chart could not be loaded.') : null}
          titleFor={(tile) =>
            tile.titleOverride ??
            chartById.get(tile.chartId)?.name ??
            t('Chart')
          }
          // The overridden spec, not the pinned one: the tile draws what was
          // actually run, which is what stops a re-scoped tile being rendered
          // with the format and kind of the chart it used to be.
          specFor={(tile) => specByTileId.get(tile.id)}
          resultFor={(tile) => resultsByTileId.get(tile.id)}
          isMissing={(tile) => !chartsLoading && !chartById.has(tile.chartId)}
          overrideNoteFor={(tile) => noteByTileId.get(tile.id) ?? null}
          bylineFor={(tile) => {
            const chart = chartById.get(tile.chartId);
            return chart?.createdBy
              ? createdByLine(
                  t,
                  i18n.language,
                  chart.createdBy,
                  chart.createdAt
                )
              : null;
          }}
          onChange={applyChange}
        />
      )}

      <DashboardNameModal
        open={nameModal !== null}
        setOpen={(open) => !open && setNameModal(null)}
        initialName={nameModal === 'rename' ? active?.name ?? '' : ''}
        title={
          nameModal === 'rename'
            ? t('Rename this dashboard')
            : t('New dashboard')
        }
        confirmLabel={nameModal === 'rename' ? t('Rename') : t('Create')}
        onConfirm={(name) => {
          const mode = nameModal;
          setNameModal(null);
          if (mode === 'rename') void rename(name);
          else void addDashboard(name);
        }}
      />

      <DashboardShareModal
        open={shareOpen}
        setOpen={setShareOpen}
        dashboard={active}
        onShared={() => void refetchDashboards()}
      />

      <ConfirmationModal
        open={confirmDelete}
        setOpen={setConfirmDelete}
        text={t('Delete “{{name}}”? The charts on it are not deleted.', {
          name: active?.name ?? '',
        })}
        warningText={
          active?.collaboratorUserIds?.length
            ? t(
                'Other people are working on this dashboard. They will lose it.'
              )
            : ''
        }
        confirmButtonText={t('Delete')}
        onConfirm={() => {
          setConfirmDelete(false);
          void remove();
        }}
        onCancel={() => setConfirmDelete(false)}
      />
      <ConfirmationModal
        open={confirmLeave}
        setOpen={setConfirmLeave}
        text={t(
          'Leave “{{name}}”? It will disappear from your dashboards until someone shares it with you again.',
          { name: active?.name ?? '' }
        )}
        confirmButtonText={t('Leave')}
        onConfirm={() => {
          setConfirmLeave(false);
          void leave();
        }}
        onCancel={() => setConfirmLeave(false)}
      />
    </div>
  );
}

/** Strips anything Apollo added, so the mutation gets only declared fields. */
/**
 * A declaration rather than a const so it can stay below the component that
 * uses it — hoisting makes the forward reference safe.
 */
function toTileInput(tile: DashboardTile) {
  return {
    id: tile.id,
    chartId: tile.chartId,
    size: tile.size,
    height: tile.height,
    titleOverride: tile.titleOverride ?? null,
    dateRangeOverride: tile.dateRangeOverride ?? null,
  };
}

export default Dashboard;
